import { useQuery } from '@apollo/client';
import React from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import ExternalBrandLogo from '../../components/ExternalBrandLogo';
import getExternalBrandLogoProps from '../../integrations/getExternalBrandLogoProps';
import EDITOR_INTEGRATION_MENTIONS from '../mentions/EditorIntegrationMentions';
import useInternalCopyPaste from '../utils/useInternalCopyPaste';
import styles from './EmbedFooter.module.scss';
import { EmbedMode, embedUrl } from './matchEmbed';
import { gql } from '../../graphql/generated/gql';

export const embedsWithNativeFooter = [
  'airtable',
  'figma',
  'google-calendar',
  'google-map',
  'lucidchart',
  'miro',
  'mode',
  'slideshare',
  'microsoft-office',
  'pitch',
  'tableau',
  'rows',
];

const GET_EMBED_QUERY = gql(`
  query getEmbedFooter($type: MentionType!, $id: ID!) {
    mention(type: $type, id: $id) {
      id
      title
    }
  }
`);

interface EmbedFooterContainerProps {
  children: React.ReactNode;
  className?: string;
  hasNativeFooter?: boolean;
}

export const EmbedFooterContainer = ({
  children,
  className,
  hasNativeFooter,
}: EmbedFooterContainerProps) => (
  <div
    className={classNames(styles.container, className, {
      [styles.hasNativeFooter]: hasNativeFooter,
    })}
  >
    {children}
  </div>
);

interface EmbedFooterLinkProps {
  children: React.ReactNode;
  link: string;
  newTab?: boolean;
}

export const EmbedFooterLink = ({
  children,
  link,
  newTab,
}: EmbedFooterLinkProps) => {
  const setElement = useInternalCopyPaste();

  if (newTab) {
    return (
      <a
        className={styles.link}
        ref={setElement}
        href={link}
        target="_blank"
        rel="nofollow noopener noreferrer ugc"
      >
        {children}
      </a>
    );
  }

  return (
    <Link className={styles.link} ref={setElement} to={link}>
      {children}
    </Link>
  );
};

interface EmbedFooterLinkTitleProps {
  children: React.ReactNode;
}

export const EmbedFooterLinkTitle = ({
  children,
}: EmbedFooterLinkTitleProps) => (
  <div className={styles.linkTitle}>{children}</div>
);

interface EmbedFooterLogoContainerProps {
  children: React.ReactNode;
}

export const EmbedFooterLogoContainer = ({
  children,
}: EmbedFooterLogoContainerProps) => (
  <div className={styles.logoContainer}>{children}</div>
);

interface EmbedFooterProps {
  id: string;
  type: string;
  params?: string;
  className?: string;
}

const EmbedFooter = ({ id, type, params, className }: EmbedFooterProps) => {
  const mentionInfo = EDITOR_INTEGRATION_MENTIONS[type];
  const { data } = useQuery(GET_EMBED_QUERY, {
    // @ts-expect-error
    variables: { id, type: mentionInfo?.apiEnum },
    skip: !mentionInfo,
  });

  const link = embedUrl({ id, type, params }, EmbedMode.FULL) || '';
  const hasNativeFooter = embedsWithNativeFooter.includes(type);
  return (
    <EmbedFooterContainer
      className={className}
      hasNativeFooter={hasNativeFooter}
    >
      <EmbedFooterLink link={link} newTab>
        <EmbedFooterLogoContainer>
          <ExternalBrandLogo {...getExternalBrandLogoProps(type)} height={16} />
        </EmbedFooterLogoContainer>
        <EmbedFooterLinkTitle>
          {data?.mention.title || link}
        </EmbedFooterLinkTitle>
      </EmbedFooterLink>
    </EmbedFooterContainer>
  );
};

export default EmbedFooter;
